import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/common'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  initiateFreshSystemCronManual,
  initiateMagentoCatalogueManualSync,
  initiatePricingCronManual,
  syncAggregatorCron,
} from 'utils/api/configApi'
import { ManualCronFields } from './utils'
import { manualCronSchema } from 'utils/schema/configurationsSchema'
import { DateTime } from 'components/common/input/dateTime'
import { ERROR_CODES } from 'utils/constants'
import { handleError } from 'utils/helpers/handleError'

const loadingTypes = {
  FRESH_SYSTEM: 'FRESH_SYSTEM',
  PRICING: 'PRICING',
  AGGREGATOR: 'AGGREGATOR',
  MAGENTO_CATALOGUE: 'MAGENTO_CATALOGUE',
  WP_RESET: 'WP_RESET',
}

const successMessage =
  'Cron trigger successful. You will be notified after completion.'

const failedMessage = 'Failed to trigger cron'

const ManualCrons = () => {
  const [loading, setLoading] = useState({
    type: loadingTypes.FRESH_SYSTEM,
    isLoading: false,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualCronFields>({
    resolver: yupResolver(manualCronSchema),
  })

  const triggerCron = async (
    loadingType: string,
    apiFn: () => Promise<any>,
    loadingMsg: string,
  ) => {
    setLoading({ type: loadingType, isLoading: true })
    const triggerToast = toast.loading(loadingMsg)

    try {
      await apiFn()
      toast.success(successMessage, { id: triggerToast })
    } catch (error) {
      handleError(error, triggerToast, {
        default: failedMessage,
        [ERROR_CODES.FTP_ALREADY_RUNNING]:
          'FTP generation is already in progress',
      })
    } finally {
      setLoading({ type: loadingType, isLoading: false })
    }
  }

  const triggerMagentoCatalogueSync = async ({
    lastUpdatedDateTime,
  }: ManualCronFields) => {
    await triggerCron(
      loadingTypes.MAGENTO_CATALOGUE,
      async () => await initiateMagentoCatalogueManualSync(lastUpdatedDateTime),
      'Triggering Magento catalogue sync..',
    )
  }

  const triggerFreshSystem = async ({
    lastUpdatedDateTime,
  }: ManualCronFields) => {
    await triggerCron(
      loadingTypes.FRESH_SYSTEM,
      async () => await initiateFreshSystemCronManual(lastUpdatedDateTime),
      'Triggering Fresh system cron..',
    )
  }

  const triggerPricing = async () => {
    await triggerCron(
      loadingTypes.PRICING,
      initiatePricingCronManual,
      'Triggering Pricing cron..',
    )
  }

  const syncAggregators = async () => {
    await triggerCron(
      loadingTypes.AGGREGATOR,
      syncAggregatorCron,
      'Initiating Aggregator Sync cron..',
    )
  }

  return (
    <>
      <p className="mb-1 mt-3 text-sm font-bold text-sidebar-primary">
        Manual crons
      </p>

      <p className="mb-2 text-[11px] text-gray-500">
        Manually trigger the cron job for updating Fresh system inventory and
        pricing.
      </p>

      <div className="mb-3 w-full mid-md:w-1/3 mid-md:pr-3">
        <DateTime
          required
          type="datetime-local"
          label="ERP Last Updated Date Time"
          name={'lastUpdatedDateTime'}
          register={register}
          error={errors.lastUpdatedDateTime?.message}
          placeholder="ERP Last Updated Date Time"
        />
      </div>

      <div className="flex flex-wrap items-center gap-5">
        <Button
          buttonText="Trigger Magento catalogue"
          size="small"
          className="!border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={handleSubmit(triggerMagentoCatalogueSync)}
          disabled={loading.isLoading}
          loading={
            loading.type === loadingTypes.MAGENTO_CATALOGUE && loading.isLoading
          }
        />

        <Button
          size="small"
          buttonText="Trigger Fresh System"
          className="w-full max-w-36 !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={handleSubmit(triggerFreshSystem)}
          disabled={loading.isLoading}
          loading={
            loading.type === loadingTypes.FRESH_SYSTEM && loading.isLoading
          }
        />

        <Button
          size="small"
          buttonText="Trigger Pricing"
          className="w-full max-w-36 !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={triggerPricing}
          disabled={loading.isLoading}
          loading={loading.type === loadingTypes.PRICING && loading.isLoading}
        />

        <Button
          size="small"
          buttonText="Sync Aggregators in Pricing"
          className="w-full max-w-max !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={syncAggregators}
          disabled={loading.isLoading}
          loading={
            loading.type === loadingTypes.AGGREGATOR && loading.isLoading
          }
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        Note: Select a date and time within the last year for ERP Last Updated
        Date Time.
      </p>
    </>
  )
}

export default ManualCrons
